import React, { useEffect, useState } from "react";
import { Col, Row, Button, Input } from "antd";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { LinkOutlined, RedoOutlined, SearchOutlined } from "@ant-design/icons";
import { AnalyticsIcon } from "../../components";
import MonitoringSchedule from "./MonitoringItemSchedule";
import ResultLogs from "./MonitoringItemResultLogList";
import ServerSidePagination from "../../components/Pagination/ServerSidePagination";
import { useAppSelector } from "../../redux/hooks";
import { authenticateGuest, getMonitorJob } from "./action";
import { ROUTES } from "../../constants";
import OTPAuthenticationModal from "./modals/OTPAuthenticationModal";
import { initApi } from "../../utils/apiUtils";
import { ENABLE_MONITOR_MODE } from "../../configs/app-config";
import GuestLinkModal from "./modals/GuestLinkModal";

const ITEMS_PER_PAGE = 10;

const mergeUniqueTestRuns = (existingRuns: any[] = [], incomingRuns: any[] = []) => {
  const uniqueRuns = new Map();

  [...existingRuns, ...incomingRuns].forEach((run) => {
    const key = run?.executionId || run?.resultId;
    if (key && !uniqueRuns.has(key)) {
      uniqueRuns.set(key, run);
    }
  });

  return Array.from(uniqueRuns.values());
};

const MonitoringJobListViewItemView = () => {
  const [job, setJob] = useState<any>();
  const [isLoading, setLoading] = useState<boolean>(true);
  const [isResultLogsLoading, setResultLogsLoading] = useState<boolean>(true);
  const [testRuns, setTestRuns] = useState<any[]>([]);
  const [page, setPage] = useState<number>(1);
  const [openOTPAuthenticationModal, setOpenOTPAuthenticationModal] =
    useState<boolean>(false);
  const [isSearching, setSearching] = useState<boolean>(false);
  const [isSearchLoad, setSearchLoad] = useState<boolean>(false);
  const [isGuestLinkModalOpen, setGuestLinkModalOpen] =
    useState<boolean>(false);

  const { monitorId, shareId } = useParams<any>();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const accountId = useAppSelector(
    ({ userData }: any) => userData?.selectedAccount?.accountId
  );
  const navigate = useNavigate();

  const fetchData = async (
    lastEvaluatedKey?: any,
    keyword?: string | undefined
  ) => {
    if (keyword) {
      setSearchLoad(true);
    } else {
      setLoading(true);
    }
    setResultLogsLoading(true);
    const data = await getMonitorJob(
      shareId,
      accountId,
      monitorId ?? "",
      JSON.stringify(lastEvaluatedKey),
      searchParams.get("isGuestUrl") === "true",
      keyword
    );
    setJob(data);
    setTestRuns(data.testRuns);
    setLoading(false);
    setSearchLoad(false);
    setResultLogsLoading(false);
  };

  const setPages = (segmentNumber: number) => {
    const startIndex = (segmentNumber - 1) * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;
    setTestRuns(job.testRuns.slice(startIndex, endIndex));
  };

  const handleNextPage = async () => {
    if (!job) return;

    const newPage = page + 1;
    const requiredItemCount = newPage * ITEMS_PER_PAGE;
    if ((job.testRuns?.length || 0) >= requiredItemCount) {
      setPage(newPage);
      return;
    }

    if (!job.lastEvaluatedKey) {
      return;
    }

    setResultLogsLoading(true);

    try {
      let mergedTestRuns = job.testRuns || [];
      const data = await getMonitorJob(
        shareId,
        accountId,
        monitorId ?? "",
        JSON.stringify(job.lastEvaluatedKey),
        searchParams.get("isGuestUrl") === "true"
      );

      if (data?.testRuns?.length) {
        mergedTestRuns = mergeUniqueTestRuns(mergedTestRuns, data.testRuns);
        const nextLastEvaluatedKey = data.lastEvaluatedKey;

        setJob((prev: any) => ({
          ...prev,
          ...data,
          totalCount:
            typeof data?.totalCount === "number"
              ? data.totalCount
              : prev?.totalCount,
          testRuns: mergedTestRuns,
          lastEvaluatedKey: nextLastEvaluatedKey,
        }));

        if (mergedTestRuns.length >= requiredItemCount) {
          setPage(newPage);
        }
      }
    } finally {
      setResultLogsLoading(false);
    }
  };

  const handlePreviousPage = () => {
    if (page > 1) {
      setPage(page - 1);
    }
  };

  const authenticateGuestUser = async (value: string) => {
    try {
      setOpenOTPAuthenticationModal(false);
      const { token } = await authenticateGuest({
        shareId,
        otp: value,
        isGuestUrl: searchParams.get("isGuestUrl"),
      });
      initApi(token);
      window.localStorage.setItem(shareId ?? "", token);
      await fetchData();
    } catch (err: any) {
      window.localStorage.removeItem(shareId ?? "");
      setOpenOTPAuthenticationModal(true);
    }
  };

  let timeout: any = null;
  const handleSearch = (keyword: string) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => {
      setSearching(keyword.length > 0);
      fetchData(undefined, keyword);
    }, 500);
  };

  useEffect(() => {
    if (page && job?.testRuns) {
      setPages(page);
    }
  }, [page, job?.testRuns]);

  useEffect(() => {
    if (shareId) {
      const token: null | string = window.localStorage.getItem(shareId);
      if (!token) {
        setOpenOTPAuthenticationModal(true);
      } else {
        initApi(token);
        fetchData();
      }
    } else if (!shareId && monitorId && accountId) {
      fetchData();
    }
  }, [monitorId, shareId, accountId]);

  useEffect(
    () => () => {
      setJob(undefined);
      setLoading(true);
      setResultLogsLoading(true);
      setTestRuns([]);
      setPage(1);
      setSearching(false);
      setSearchLoad(false);
    },
    []
  );

  const totalPages =
    typeof job?.totalCount === "number"
      ? Math.max(1, Math.ceil(job.totalCount / ITEMS_PER_PAGE))
      : job?.lastEvaluatedKey
      ? page + 1
      : Math.max(1, Math.ceil((job?.testRuns?.length || 0) / ITEMS_PER_PAGE));

  const canGoNext =
    typeof job?.totalCount === "number"
      ? page < totalPages
      : Boolean(job?.lastEvaluatedKey);

  const handleAnalyticsButton = () => {
    navigate(
      shareId
        ? `${ROUTES.SHARE_ANALYTICS}/${shareId}?isGuestUrl=${searchParams.get(
            "isGuestUrl"
          )}`
        : `${ROUTES.ANALYTICS}/${monitorId}`
    );
  };

  return (
    <div className="fp_pg_base fp_test_exec_suit">
      <Row>
        <Col span={ENABLE_MONITOR_MODE ? 19 : 20}>
          {!shareId && (
            <Button
              data-testid="fp_previous_btn"
              className="back_btn fp_btn-primary fp_btn"
              onClick={() => navigate(-1)}
            >
              Back
            </Button>
          )}
        </Col>
      </Row>
      <Row
        style={{
          marginTop:
            searchParams.get("isGuestUrl") === "true" ? "50px" : "-50px",
          textAlign: "center",
        }}
        justify="center"
      >
        <Col span={18}>
          <p className="fp_heading-2 fp_heading-2-middle">{job?.name}</p>
        </Col>
      </Row>
      <Row justify="end">
        <Col>
          <Button
            className="fp_btn fp_btn-refresh-btn"
            type="primary"
            icon={<RedoOutlined />}
            onClick={() => fetchData()}
          >
            Refresh
          </Button>
        </Col>
        {accountId && !ENABLE_MONITOR_MODE && (
          <Col style={{ marginLeft: 20 }}>
            <Button
              className="fp_btn fp_btn-refresh-btn"
              type="primary"
              icon={<LinkOutlined />}
              onClick={() => setGuestLinkModalOpen(true)}
            >
              Share
            </Button>
          </Col>
        )}
        <Col style={{ marginLeft: 20 }}>
          <Button
            className="fp_btn fp_btn-analytics"
            type="primary"
            icon={<AnalyticsIcon />}
            onClick={handleAnalyticsButton}
          >
            Analytics
          </Button>
        </Col>
      </Row>
      <Row>
        <p className="fp_heading-2 fp_heading-2-left">Monitoring Schedule</p>
      </Row>
      <MonitoringSchedule isLoading={isLoading} job={job} />
      <Row>
        <p className="fp_heading-2 fp_heading-2-left">Result logs</p>
      </Row>
      <Row justify="end">
        <Col span={6}>
          <Input
            prefix={<SearchOutlined />}
            className="fp_monitor_job_results_log_search"
            placeholder="Search"
            onChange={(text) => handleSearch(text.target.value)}
            data-testid="fp_monitor_job_results_log_search"
            disabled={isLoading}
          />
        </Col>
      </Row>
      <ResultLogs
        isLoading={isResultLogsLoading || isSearchLoad}
        job={job}
        testRuns={testRuns}
        isShared={!!shareId}
        isSearching={isSearching}
        handleRefresh={fetchData}
      />
      {!isSearching && !isResultLogsLoading && !isSearchLoad && (
        <ServerSidePagination
          currentPage={page}
          totalPages={totalPages}
          canGoPrevious={page > 1}
          canGoNext={canGoNext}
          onPrevious={handlePreviousPage}
          onNext={handleNextPage}
          isLoading={isResultLogsLoading}
        />
      )}
      <OTPAuthenticationModal
        open={openOTPAuthenticationModal}
        onOkay={authenticateGuestUser}
        onCancel={setOpenOTPAuthenticationModal}
      />
      <GuestLinkModal
        open={isGuestLinkModalOpen}
        onCancel={() => setGuestLinkModalOpen(false)}
      />
    </div>
  );
};
export default MonitoringJobListViewItemView;
